import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { documentQueryKeys } from "../api/documents";
import { RpcError, rpc } from "../api/rpc";
import type { Doc } from "../api/types";
import type { DocumentSession, DocumentSessionInput, SaveStatus } from "./session";

/**
 * Non-realtime saving (`docs/spec/documents.md` §3.3).
 *
 * Changes are coalesced: a save fires one second after the last keystroke, and
 * never more than ten seconds after the first unsaved change, so a long stretch
 * of typing still reaches the server at a steady rate. Every save carries
 * `lastRevision`, so a document edited elsewhere in the meantime is refused
 * rather than overwritten; leaving the document sends `done: true`, which is
 * what asks the server for a revision snapshot.
 *
 * This is one of the two implementations of `DocumentSession`; the other is the
 * collaborative transport. Everything the screen needs is in the return value,
 * and nothing in here reaches into the editor beyond `readMarkdown`.
 */
export function useDocumentEditorSession(input: DocumentSessionInput): DocumentSession {
  const {
    documentId,
    revision,
    initialTitle,
    readOnly,
    readMarkdown,
    onConflict,
    onSaved,
    onError,
    debounceMs = 1000,
    maxWaitMs = 10_000,
    sendLastRevision = true,
  } = input;

  const client = useQueryClient();
  const [title, setTitleState] = useState(initialTitle);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const titleRef = useRef(initialTitle);
  const revisionRef = useRef(revision);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstPendingRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(false);
  const readRef = useRef(readMarkdown);
  const callbacksRef = useRef({ onConflict, onSaved, onError });

  // Kept current after every render rather than during it: the values are only
  // ever read from a timer or an in-flight save, both of which run after paint.
  useEffect(() => {
    readRef.current = readMarkdown;
    callbacksRef.current = { onConflict, onSaved, onError };
  });

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const save = useCallback(
    async (done: boolean): Promise<void> => {
      clearTimer();
      if (readOnly) return;
      if (!dirtyRef.current) return;
      if (inFlightRef.current) {
        // A save is already on the wire; the change it did not carry re-arms
        // the timer when it lands.
        return;
      }

      const text = readRef.current();
      dirtyRef.current = false;
      firstPendingRef.current = null;
      inFlightRef.current = true;
      setStatus("saving");

      try {
        const result = await rpc<Doc>("documents.update", {
          id: documentId,
          title: titleRef.current,
          ...(text === null ? {} : { text }),
          ...(sendLastRevision ? { lastRevision: revisionRef.current } : {}),
          ...(done ? { done: true } : {}),
        });
        revisionRef.current = result.data.revision;
        client.setQueryData(documentQueryKeys.info(documentId), {
          document: result.data,
          abilities:
            result.policies.find((policy) => policy.id === result.data.id)?.abilities ?? {},
        });
        void client.invalidateQueries({ queryKey: ["documents"], exact: false });
        setStatus("saved");
        setSavedAt(Date.now());
        callbacksRef.current.onSaved?.(result.data);
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error("The document could not be saved");
        if (
          cause instanceof RpcError &&
          (cause.status === 409 || cause.id === "document_conflict")
        ) {
          setStatus("conflict");
          const data: unknown = cause.data;
          const raw =
            typeof data === "object" && data !== null && "revision" in data
              ? Number(data.revision)
              : Number.NaN;
          callbacksRef.current.onConflict(Number.isNaN(raw) ? undefined : raw);
        } else {
          setStatus("error");
          dirtyRef.current = true;
          callbacksRef.current.onError?.(error);
        }
      } finally {
        inFlightRef.current = false;
      }
    },
    [client, documentId, readOnly, sendLastRevision],
  );

  const schedule = useCallback(() => {
    if (readOnly) return;
    dirtyRef.current = true;
    setStatus("pending");
    const now = Date.now();
    firstPendingRef.current ??= now;
    const deadline = firstPendingRef.current + maxWaitMs;
    const delay = Math.max(0, Math.min(debounceMs, deadline - now));
    clearTimer();
    timerRef.current = setTimeout(() => {
      void save(false);
    }, delay);
  }, [debounceMs, maxWaitMs, readOnly, save]);

  const setTitle = useCallback(
    (value: string) => {
      titleRef.current = value;
      setTitleState(value);
      schedule();
    },
    [schedule],
  );

  const finish = useCallback(() => {
    if (!dirtyRef.current) return;
    void save(true);
  }, [save]);

  // Leaving the document is the other half of `done: true`: the last edit is
  // flushed, and the server takes its revision snapshot there rather than
  // waiting out a debounce nobody is left to finish.
  useEffect(
    () => () => {
      clearTimer();
      if (dirtyRef.current && !readOnly) {
        void save(true);
      }
    },
    [readOnly, save],
  );

  return {
    title,
    setTitle,
    noteChange: schedule,
    finish,
    status,
    savedAt,
    realtime: false,
  };
}
